import { takeLatest } from 'redux-saga/effects';
import { call, put, select } from 'typed-redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';

import { handleServerError } from '@crm-framework/utils';
import { getUser } from '@crm-framework/auth';

import {
  loadAndSaveDictionariesByName,
  loadAndSaveDictionariesByType,
} from '@crm-package/dictionaries';

import { apiGetLead } from './api';
import { leadShortInfoActions } from './store';
import { LeadIdType } from './types';
import {
  checkRightsForWatchLeadInfo,
  getLeadShortInfoState,
  leadInfoMapper,
} from './utils';

function* checkUserRightForWatchLeadInfo() {
  const manager = yield* select(getUser);

  if (!manager) return false;

  const canWatch = checkRightsForWatchLeadInfo(manager.rights);
  yield put(leadShortInfoActions.setHasRightFortWatchInfo(canWatch));

  return canWatch;
}

function* loadLeadInfo({ payload: leadId }: PayloadAction<LeadIdType>) {
  try {
    const { hasRightForWatchInfo } = yield* select(getLeadShortInfoState);

    if (hasRightForWatchInfo) {
      yield put(leadShortInfoActions.setInitLeadState());
      yield put(leadShortInfoActions.setIsLoading(true));
      const { country: countries } = yield* loadAndSaveDictionariesByType([
        'country',
      ]);

      const { language: languages, businessUnit: businessUnits } =
        yield* loadAndSaveDictionariesByName(['language', 'businessUnit']);

      const leadData = yield* call(apiGetLead, leadId);

      const transformedLeadData = leadInfoMapper({
        lead: leadData,
        languages,
        businessUnits,
        countries,
      });

      yield put(leadShortInfoActions.setLeadData(transformedLeadData));
    }
  } catch (e) {
    if (e.status === 403) {
      yield put(leadShortInfoActions.setIsPermissionError(true));

      return;
    }

    handleServerError(e);
  } finally {
    yield put(leadShortInfoActions.setIsLoading(false));
  }
}

export function* watchShortLeadInfo() {
  yield checkUserRightForWatchLeadInfo(); // call with first watchers init
  yield takeLatest(leadShortInfoActions.leadLoad.type, loadLeadInfo);
}
